import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, LogOut, RefreshCw, X, Search, FileText, Loader2 } from "lucide-react";
import api, { API, LOGO_URL } from "../lib/api";
import { toast } from "sonner";

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function ResponseDrawer({ id, onClose }) {
  const [data, setData] = useState(null);
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([api.get(`/admin/responses/${id}`), api.get("/form/schema")])
      .then(([r, s]) => {
        setData(r.data);
        setSchema(s.data);
      })
      .catch(() => toast.error("Failed to load response"))
      .finally(() => setLoading(false));
  }, [id]);

  if (!id) return null;

  return (
    <div className="fixed inset-0 z-40" data-testid="response-detail-drawer">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <aside className="absolute right-0 top-0 bottom-0 w-full max-w-2xl bg-[color:var(--rivot-surface)] border-l border-zinc-800 overflow-y-auto">
        <div className="sticky top-0 backdrop-blur-xl bg-black/60 border-b border-white/10 px-8 py-5 flex items-center justify-between">
          <div>
            <div className="font-mono-rivot text-[11px] uppercase tracking-[0.3em] text-[color:var(--rivot-orange)]">
              Application
            </div>
            <div className="font-display text-lg text-white mt-1 truncate max-w-xs">
              {data?.answers?.full_name || (loading ? "Loading…" : "Response")}
            </div>
          </div>
          <button
            className="rivot-btn-ghost"
            onClick={onClose}
            data-testid="close-drawer-button"
          >
            <X size={14} /> Close
          </button>
        </div>

        <div className="p-8">
          {loading || !data || !schema ? (
            <div className="flex items-center gap-3 text-zinc-400">
              <Loader2 className="animate-spin" size={16} /> Loading application…
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4 mb-8 text-sm">
                <div>
                  <div className="text-[10px] font-mono-rivot uppercase tracking-widest text-zinc-500">
                    Response ID
                  </div>
                  <div className="text-zinc-200 font-mono-rivot text-xs mt-1 break-all">
                    {data.id}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-mono-rivot uppercase tracking-widest text-zinc-500">
                    Submitted
                  </div>
                  <div className="text-zinc-200 mt-1">{formatDate(data.submitted_at)}</div>
                </div>
              </div>

              <div className="divide-y divide-zinc-800">
                {schema.fields.map((f, i) => {
                  let v = data.answers?.[f.ref];
                  if (f.type === "yes_no") v = v === true ? "Yes" : v === false ? "No" : "—";
                  if (v === undefined || v === null || v === "") v = "—";
                  return (
                    <div key={f.ref} className="py-5 grid gap-2" data-testid={`answer-${f.ref}`}>
                      <div className="text-[10px] font-mono-rivot uppercase tracking-widest text-zinc-500">
                        Q{String(i + 1).padStart(2, "0")} · {f.type}
                      </div>
                      <div className="text-sm text-zinc-400">{f.title}</div>
                      <div className="text-base text-white whitespace-pre-wrap">{String(v)}</div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

export default function AdminDashboard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/responses");
      setItems(data.items || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("rivot_admin_token");
        navigate("/admin/login", { replace: true });
        return;
      }
      toast.error("Failed to load responses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter(
      (r) =>
        (r.full_name || "").toLowerCase().includes(q) ||
        (r.email || "").toLowerCase().includes(q) ||
        (r.mobile || "").toLowerCase().includes(q) ||
        (r.preferred_city || "").toLowerCase().includes(q)
    );
  }, [items, query]);

  const logout = () => {
    localStorage.removeItem("rivot_admin_token");
    localStorage.removeItem("rivot_admin_user");
    navigate("/admin/login", { replace: true });
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("rivot_admin_token");
      const resp = await fetch(`${API}/admin/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rivot-responses-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem("rivot_admin_user") || "null");
    } catch {
      return null;
    }
  })();

  return (
    <div className="min-h-screen bg-[color:var(--rivot-bg)]">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="px-6 md:px-10 py-4 flex items-center gap-6">
          <img src={LOGO_URL} alt="RIVOT Motors" className="h-7 opacity-90" data-testid="admin-logo" />
          <div className="hidden md:block border-l border-zinc-800 pl-6">
            <div className="font-mono-rivot text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              Dealership Applications
            </div>
            <div className="font-display text-sm text-white">Admin Dashboard</div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <div className="hidden sm:block text-right">
              <div className="text-[10px] font-mono-rivot uppercase tracking-widest text-zinc-500">
                Signed in
              </div>
              <div className="text-sm text-zinc-200" data-testid="admin-user-email">
                {user?.email || "—"}
              </div>
            </div>
            <button className="rivot-btn-ghost" onClick={logout} data-testid="admin-logout-button">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="px-6 md:px-10 py-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <div className="font-mono-rivot text-xs uppercase tracking-[0.3em] text-[color:var(--rivot-orange)]">
              Responses
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-light text-white tracking-tight mt-2">
              {loading ? "—" : items.length}{" "}
              <span className="text-zinc-500 text-2xl">applications</span>
            </h1>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 border border-zinc-800 px-3 py-2 bg-black/40 focus-within:border-[color:var(--rivot-orange)] transition-colors">
              <Search size={14} className="text-zinc-500" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, email, city…"
                className="bg-transparent outline-none text-sm text-white placeholder:text-zinc-600 w-64"
                data-testid="admin-search-input"
              />
            </div>
            <button
              className="rivot-btn-ghost"
              onClick={load}
              data-testid="admin-refresh-button"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              className="rivot-btn"
              onClick={exportCsv}
              disabled={exporting || items.length === 0}
              data-testid="admin-export-button"
            >
              {exporting ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Download size={16} />
              )}
              Export CSV
            </button>
          </div>
        </div>

        <div className="border border-zinc-800/80 overflow-x-auto">
          <table className="w-full text-sm" data-testid="responses-table">
            <thead>
              <tr className="bg-zinc-900/80 text-left text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-mono-rivot">
                <th className="px-5 py-3 border-b border-zinc-800 w-16">#</th>
                <th className="px-5 py-3 border-b border-zinc-800">Applicant</th>
                <th className="px-5 py-3 border-b border-zinc-800">Contact</th>
                <th className="px-5 py-3 border-b border-zinc-800">City</th>
                <th className="px-5 py-3 border-b border-zinc-800">Investment</th>
                <th className="px-5 py-3 border-b border-zinc-800">Submitted</th>
                <th className="px-5 py-3 border-b border-zinc-800 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-5 py-16 text-center text-zinc-500">
                    <Loader2 className="inline animate-spin mr-2" size={14} />
                    Loading applications…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-16 text-center text-zinc-500">
                    {items.length === 0
                      ? "No applications yet. Share the form to start collecting responses."
                      : "No results match your search."}
                  </td>
                </tr>
              ) : (
                filtered.map((r, i) => (
                  <tr
                    key={r.id}
                    className="border-b border-zinc-900 hover:bg-zinc-900/60 transition-colors cursor-pointer"
                    onClick={() => setSelectedId(r.id)}
                    data-testid={`response-row-${r.id}`}
                  >
                    <td className="px-5 py-4 text-zinc-500 font-mono-rivot text-xs">
                      {String(i + 1).padStart(3, "0")}
                    </td>
                    <td className="px-5 py-4 text-white font-display">
                      {r.full_name || <span className="text-zinc-600">—</span>}
                    </td>
                    <td className="px-5 py-4 text-zinc-300">
                      <div>{r.email || "—"}</div>
                      <div className="text-xs text-zinc-500">{r.mobile || ""}</div>
                    </td>
                    <td className="px-5 py-4 text-zinc-300">{r.preferred_city || "—"}</td>
                    <td className="px-5 py-4 text-zinc-300">{r.investment_capacity || "—"}</td>
                    <td className="px-5 py-4 text-zinc-400 text-xs font-mono-rivot">
                      {formatDate(r.submitted_at)}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <span className="inline-flex items-center gap-1 text-[color:var(--rivot-orange)] text-xs font-mono-rivot uppercase tracking-widest">
                        <FileText size={12} /> View
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>

      <ResponseDrawer id={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
}
